import React, { useCallback, useEffect, useState } from 'react'
import { CCard, CCardBody, CFormInput, CFormSelect, CInputGroup } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilSearch } from '@coreui/icons'
import CommunityPostsService from 'src/service/CommunityPostsService'
import CommunityPostsTable, { getColumns } from './CommunityPostsTable'
import './CommunityPosts.css'

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'system', label: 'System' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
]

// Shared shell for every single-type community posts page (round winner, top teams, …).
const CommunityPostsListing = ({ postType, title, subtitle }) => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')

  // The API searches payload->title, so only offer the box where the table shows a title.
  const titleSearchable = getColumns(postType).some((column) => column.key === 'title')

  const fetchPosts = useCallback(async () => {
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    // This endpoint reads sort_by/sort_order, not the sort=column%state form used elsewhere.
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort_by', columnSorter.column)
      params.append('sort_order', columnSorter.state)
    }

    setLoading(true)
    try {
      const result = await CommunityPostsService.getCommunityPosts(
        postType,
        offset,
        itemsPerPage,
        activePage,
        params,
      )
      setPosts(result.data)
    } catch (error) {
      console.error('Error fetching community posts:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter, postType])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if ((columnFilter.title || '') === term) return
      setActivePage(1)
      setColumnFilter((current) => {
        const next = { ...current }
        if (term) next.title = term
        else delete next.title
        return next
      })
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, columnFilter])

  const handleSourceChange = (value) => {
    setActivePage(1)
    setColumnFilter((current) => {
      const next = { ...current }
      if (value) next.source = value
      else delete next.source
      return next
    })
  }

  const total = posts?.total ?? 0

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">{title}</h1>
        {subtitle && <p className="ge-page-subtitle">{subtitle}</p>}
      </div>

      <CCard className="cp-panel">
        <CCardBody>
          <div className="cp-panel-toolbar">
            <div>
              <h2 className="cp-panel-title">{title}</h2>
              <p className="cp-panel-note">
                {total} {total === 1 ? 'post' : 'posts'}
              </p>
            </div>
            <div className="cp-panel-actions">
              <CFormSelect
                className="cp-type-select"
                aria-label="Filter by source"
                value={columnFilter.source || ''}
                onChange={(e) => handleSourceChange(e.target.value)}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CFormSelect>

              {titleSearchable && (
                <CInputGroup className="ge-search">
                  <span className="ge-search-icon">
                    <CIcon icon={cilSearch} />
                  </span>
                  <CFormInput
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by title..."
                    aria-label="Search posts by title"
                  />
                </CInputGroup>
              )}
            </div>
          </div>

          <CommunityPostsTable
            posts={posts}
            postType={postType}
            loading={loading}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
          />
        </CCardBody>
      </CCard>
    </>
  )
}

export default CommunityPostsListing
